import { ThemeEvents, CartUpdateEvent, CartGroupedSections } from "@theme/events";
import { getSectionId } from "@theme/utilities";
import { getDriver, REDUCED } from "@theme/1scp-scroll";
import { rollText, rolledText, amount, ROLL_TIME } from "@theme/1scp-roll";

// Just long enough for the buy button's tick and "Added" to register before the bag covers it; an
// add should feel instant, so this is kept short.
const ADDED_PAUSE = 450;

// Long enough for the plan toggle's slide and the offer's tick to finish before the bag redraws.
const SWITCH_PAUSE = 350;

// Footer blocks that come and go with the bag's contents; they fold open or shut instead of popping.
const FOLDING = ".scpd-cart__offer, .scpd-cart__offers";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, REDUCED.matches ? 0 : ms));

class Cart extends HTMLElement {
  #dialog = null;
  #opener = null;
  #status = null;

  connectedCallback() {
    this.#dialog = this.querySelector("dialog");
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener(ThemeEvents.cartError, this.#onCartError);
    document.addEventListener(CartGroupedSections.eventName, this.#onGrouped);
    document.addEventListener("click", this.#onBagLink);
    document.addEventListener("keydown", this.#onKey);
    document.addEventListener("shopify:section:select", this.#onEditorSelect);
    document.addEventListener("shopify:section:deselect", this.#onEditorDeselect);
    this.addEventListener("click", this.#onClick);
    // Scroll does not bubble, so it is caught on the way down; the list itself is replaced on every render.
    this.addEventListener("scroll", this.#onScroll, { capture: true, passive: true });

    // A click on the backdrop lands on the dialog element itself.
    this.#dialog?.addEventListener("click", this.#onBackdrop);
    this.#dialog?.addEventListener("close", this.#onClosed);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener(ThemeEvents.cartError, this.#onCartError);
    document.removeEventListener(CartGroupedSections.eventName, this.#onGrouped);
    document.removeEventListener("click", this.#onBagLink);
    document.removeEventListener("keydown", this.#onKey);
    document.removeEventListener("shopify:section:select", this.#onEditorSelect);
    document.removeEventListener("shopify:section:deselect", this.#onEditorDeselect);
  }

  // The drawer is closed by default, so the theme editor opens it while its section is being edited.
  #onEditorSelect = (event) => {
    if (event.target.contains(this)) this.open();
  };

  #onEditorDeselect = (event) => {
    if (event.target.contains(this)) this.close();
  };

  get #sectionId() {
    return getSectionId(this);
  }

  get #root() {
    return window.Shopify?.routes?.root || "/";
  }

  /** Called by the header's bag link (`on:click="#CartDrawer/open"`). */
  open(event) {
    event?.preventDefault?.();
    if (!this.#dialog || this.#dialog.open) return;
    this.#opener = document.activeElement;
    this.#dialog.showModal();
    this.#fade(this.querySelector("[data-scpd-cart-scroll]"));
    getDriver().lenis?.stop();
    document.documentElement.style.overflow = "hidden";
  }

  close() {
    this.#dialog?.close();
    this.#onClosed();
  }

  /**
   * The theme's `on:click` only reaches its own components, so the click is taken on the document,
   * which also survives the header being re-rendered.
   */
  #onBagLink = (event) => {
    const link = event.target.closest?.(".cart-drawer-button, [aria-controls='cart-drawer-dialog']");
    if (!link || this.contains(link)) return;
    if (location.pathname.replace(/\/$/, "").endsWith("/cart")) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey) return;
    this.open(event);
    // The bag may have changed in another tab, so it is fetched fresh on open.
    this.#refresh();
  };

  /**
   * Runs from `close()` and again from the dialog's close event, so it must be safe to run twice.
   * The event alone is not enough, because a background tab may hold it back.
   */
  #onClosed = () => {
    if (this.#dialog?.open) return;
    getDriver().lenis?.start();
    document.documentElement.style.overflow = "";
    if (this.#opener?.isConnected) this.#opener.focus({ preventScroll: true });
    this.#opener = null;
  };

  /**
   * The dialog handles Escape only while focus is inside it, and after a body swap the focused
   * control may be gone, so the key is taken on the document too.
   */
  #onScroll = (event) => {
    if (event.target.matches?.("[data-scpd-cart-scroll]")) this.#fade(event.target);
  };

  #fade(list) {
    if (!list) return;
    list.classList.toggle("can-scroll-up", list.scrollTop > 2);
    list.classList.toggle("can-scroll-down", list.scrollTop + list.clientHeight < list.scrollHeight - 2);
  }

  #onKey = (event) => {
    if (event.key === "Escape" && this.#dialog?.open) {
      event.preventDefault();
      this.close();
    }
  };

  #onBackdrop = (event) => {
    if (event.target === this.#dialog) this.close();
  };

  #onGrouped = (event) => {
    event.detail.sections.push(this.#sectionId);
  };

  #pendingError = null;

  #onCartUpdate = (event) => {
    const data = event.detail?.data ?? {};
    if (data.source === "scpd-cart") return;

    // The theme's forms send an update right after an error, and the render would wipe the reason.
    const sayAgain = () => {
      if (!this.#pendingError) return;
      this.#say(null, this.#pendingError, true);
      this.#pendingError = null;
    };
    const html = data.sections?.[this.#sectionId];
    if (html) {
      this.#render(html);
      sayAgain();
    } else this.#refresh().then(sayAgain);

    if (!data.isIncremental || data.didError) return;
    this.#pendingError = null;
    const fromBuyButton = event.target?.closest?.("scpd-pdp-hero, scpd-buy-bar");
    if (fromBuyButton && !REDUCED.matches) setTimeout(() => this.open(), ADDED_PAUSE);
    else this.open();
  };

  // The theme's forms fail silently (for example when the stock limit is already in the bag), so the
  // bag opens and says why.
  #onCartError = (event) => {
    const { description, message } = event.detail?.data ?? {};
    this.#pendingError = description || message || this.dataset.errorText;
    this.open();
    this.#say(null, this.#pendingError, true);
  };

  #onClick = (event) => {
    const target = event.target.closest("button");
    if (!target || !this.contains(target)) return;

    if (target.hasAttribute("data-scpd-close")) {
      this.close();
      return;
    }

    const qtyKey = target.dataset.scpdQty;
    if (qtyKey) {
      const line = this.#line(qtyKey);
      const current = Number(line?.dataset.quantity) || 0;
      const next = Math.max(0, current + Number(target.dataset.delta || 0));
      this.#change(qtyKey, { quantity: next });
      return;
    }

    const removeKey = target.dataset.scpdRemove;
    if (removeKey) {
      this.#change(removeKey, { quantity: 0 });
      return;
    }

    const planKey = target.dataset.scpdPlan ?? target.dataset.scpdOneTime;
    if (planKey) {
      this.#switchPlan(planKey, target);
      return;
    }

    const variantId = target.dataset.scpdAdd;
    if (variantId) this.#add(variantId, target);
  };

  #line(key) {
    return this.querySelector(`[data-scpd-line="${CSS.escape(key)}"]`);
  }

  async #change(key, fields) {
    const line = this.#line(key);
    const stepping = fields.quantity > 0;

    // A step up or down shows at once: the number rolls to its new value while Shopify answers, and
    // the line stays at full strength, so the bag never feels like it is waiting. The price and the
    // subtotal follow with the answer. Removing a line still dims it, because it is about to go.
    let rolled = null;
    if (stepping && line) {
      line.classList.add("is-busy");
      const cell = line.querySelector(".scpd-cart__qty-value");
      const from = Number(line.dataset.quantity) || 0;
      line.dataset.pendingQuantity = String(fields.quantity);
      rollText(cell, String(fields.quantity), fields.quantity < from ? -1 : 1);
      rolled = wait(ROLL_TIME);
    } else {
      line?.classList.add("is-updating");
    }

    // A merged line stands for several of Shopify's lines: the first takes the new total and the rest go
    // to zero. /cart/change.js is not used here because Shopify then splits a unit off for its discounts
    // on top of the quantity asked, while /cart/update.js keeps the total.
    const siblings = (line?.dataset.siblings || "").split(",").filter(Boolean);
    const updates = Object.fromEntries([[key, fields.quantity], ...siblings.map((sibling) => [sibling, 0])]);
    const [{ ok, body }] = await Promise.all([this.#post(this.#root + "cart/update.js", { updates }), rolled]);

    if (!ok) {
      await this.#refresh();
      this.#say(this.#line(key), body?.description || body?.message || this.dataset.errorText, true);
      return;
    }

    if (fields.quantity === 0) {
      // The last line leaving takes the totals and checkout with it, so they fade as it folds
      // rather than vanishing when the empty bag is drawn.
      const last = this.querySelectorAll("[data-scpd-line]").length === 1;
      const foot = last ? this.querySelector("[data-scpd-cart-body] .scpd-cart__foot") : null;
      await Promise.all([this.#collapse(line), this.#fadeOut(foot)]);
    }
    this.#render(body.sections?.[this.#sectionId], { animate: true });
    this.#announce(body.item_count);

    // Shopify may add fewer than asked without an error while the product keeps selling, so compare.
    if (fields.quantity > 0 && line) {
      const variant = Number(line.dataset.variant);
      const plan = Number(line.dataset.plan) || null;
      const total = (body.items || [])
        .filter((item) => item.variant_id === variant && !item.properties?._free)
        .filter((item) => (item.selling_plan_allocation?.selling_plan.id ?? null) === plan)
        .reduce((sum, item) => sum + item.quantity, 0);
      if (total < fields.quantity) this.#say(this.#line(key), this.dataset.limitText, true);
    }
  }

  /**
   * One `cart/change.js` on the line's key moves it onto the plan or off it (`selling_plan: null`),
   * keeping the line, its properties and, because it is sent along, its quantity. Without the
   * quantity Shopify resets the line to one.
   */
  async #switchPlan(key, button) {
    const line = this.#line(key);
    line?.classList.add("is-updating");
    button.closest(".scpd-cart__offer")?.classList.add("is-switching");
    const slid = wait(SWITCH_PAUSE);

    const quantity = Number(button.dataset.quantity) || 1;
    const planId = button.dataset.planId;

    // The switch at the foot carries every one-time line that can go on the plan ("key|qty|plan,"),
    // so they move together. A line's own switch back to one-time carries only itself. The calls
    // go one after another: a line's key changes when its plan does, the others' keys do not.
    const lines = (button.dataset.scpdPlanLines || "")
      .split(",")
      .filter(Boolean)
      .map((entry) => {
        const [id, qty, plan] = entry.split("|");
        return { id, quantity: Number(qty) || 1, plan };
      });
    if (!lines.length) lines.push({ id: key, quantity, plan: planId });
    for (const entry of lines) this.#line(entry.id)?.classList.add("is-updating");

    let added;
    for (const entry of lines) {
      added = await this.#post(this.#root + "cart/change.js", {
        id: entry.id,
        quantity: entry.quantity,
        selling_plan: entry.plan ? Number(entry.plan) : null,
      });
      if (!added.ok) break;
    }

    if (!added.ok) {
      await this.#refresh();
      this.#say(null, added.body?.description || added.body?.message || this.dataset.errorText);
      this.#announce();
      return;
    }

    await slid;
    const html = added.body.sections?.[this.#sectionId];
    if (html && !this.#parse(html)?.querySelector(".scpd-cart__offer")) {
      await this.#collapse(this.querySelector("[data-scpd-cart-body] .scpd-cart__offer"));
    }
    this.#render(html, { animate: true });
    this.#announce();
  }

  async #add(variantId, button) {
    button.setAttribute("aria-busy", "true");
    button.classList.add("is-adding");
    const { ok, body } = await this.#post(this.#root + "cart/add.js", {
      items: [{ id: Number(variantId), quantity: 1 }],
    });
    button.removeAttribute("aria-busy");
    button.classList.remove("is-adding");

    if (!ok) {
      this.#say(null, body?.description || body?.message || this.dataset.errorText);
      return;
    }

    button.classList.add("is-added");
    await wait(SWITCH_PAUSE);
    button.closest(".scpd-cart-offer")?.classList.add("is-leaving");
    await wait(150);
    this.#render(body.sections?.[this.#sectionId], { animate: true });
    this.#announce();
  }

  async #post(url, payload) {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ...payload, sections: [this.#sectionId], sections_url: location.pathname }),
      });
      const body = await response.json().catch(() => null);
      return { ok: response.ok && !body?.status, body };
    } catch (error) {
      console.error(error);
      return { ok: false, body: null };
    }
  }

  async #refresh() {
    try {
      const url = `${location.pathname}?sections=${encodeURIComponent(this.#sectionId)}`;
      const response = await fetch(url);
      const sections = await response.json();
      this.#render(sections[this.#sectionId]);
    } catch (error) {
      console.error(error);
    }
  }

  #render(html, { animate = false } = {}) {
    if (!html) return;
    const fresh = this.#parse(html);
    const current = this.querySelector("[data-scpd-cart-body]");
    if (!fresh || !current) return;
    const before = animate && this.#dialog?.open ? this.#snapshot(current) : null;

    const scroll = current.querySelector("[data-scpd-cart-scroll]")?.scrollTop ?? 0;
    const focused = document.activeElement;
    const focusKey = focused && current.contains(focused) ? this.#focusKey(focused) : null;

    current.replaceWith(fresh);

    const list = fresh.querySelector("[data-scpd-cart-scroll]");
    if (list) list.scrollTop = scroll;
    this.#fade(list);
    if (before) this.#animateFrom(before, fresh, list);

    if (this.#dialog?.open) {
      const again = focusKey ? fresh.querySelector(focusKey) : null;
      (again && !again.disabled ? again : fresh.querySelector("[data-scpd-close]"))?.focus({ preventScroll: true });
    }
  }

  #parse(html) {
    return new DOMParser().parseFromString(html, "text/html").querySelector("[data-scpd-cart-body]");
  }

  // Folds an element away before the body is swapped, so what follows closes the gap instead of jumping.
  // The parent's gap is folded too, otherwise it would snap shut on render.
  async #collapse(element) {
    if (!element || REDUCED.matches) return;
    const parent = element.parentElement;
    const gap = parent ? parseFloat(getComputedStyle(parent).rowGap) || 0 : 0;
    // The gap after the element folds away, or the one before it when it is the last child.
    const side = element.nextElementSibling ? "marginBottom" : "marginTop";
    element.style.overflow = "hidden";
    element.style.pointerEvents = "none";
    await element.animate(
      [
        { height: `${element.offsetHeight}px`, opacity: 1, [side]: "0px" },
        { opacity: 0, offset: 0.4 },
        { height: "0px", opacity: 0, [side]: `${-gap}px`, paddingTop: "0px", paddingBottom: "0px" },
      ],
      { duration: 350, easing: "cubic-bezier(0.65, 0, 0.35, 1)", fill: "forwards" }
    ).finished.catch(() => {});
  }

  async #fadeOut(element) {
    if (!element || REDUCED.matches) return;
    element.style.pointerEvents = "none";
    await element.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 350,
      easing: "cubic-bezier(0.65, 0, 0.35, 1)",
      fill: "forwards",
    }).finished.catch(() => {});
  }

  #snapshot(body) {
    const lines = new Map();
    for (const line of body.querySelectorAll("[data-scpd-line]")) {
      lines.set(line.dataset.scpdLine, {
        price: rolledText(line.querySelector(".scpd-cart__price > span")),
        // A step already rolled its number ahead of the answer, so it is not rolled a second time.
        qty: line.dataset.pendingQuantity ?? rolledText(line.querySelector(".scpd-cart__qty-value")),
      });
    }
    return {
      lines,
      total: rolledText(body.querySelector(".scpd-cart__sum dd")),
      blocks: new Set([...body.querySelectorAll(FOLDING)].map((block) => block.className)),
      offers: new Set([...body.querySelectorAll("[data-scpd-add]")].map((button) => button.dataset.scpdAdd)),
    };
  }

  #expand(element) {
    if (!element || REDUCED.matches || !element.offsetHeight) return;
    const parent = element.parentElement;
    const gap = parent && element.nextElementSibling ? parseFloat(getComputedStyle(parent).rowGap) || 0 : 0;
    element.style.overflow = "hidden";
    element.animate(
      [
        { height: "0px", opacity: 0, marginBottom: `${-gap}px`, paddingTop: "0px", paddingBottom: "0px" },
        { opacity: 0, offset: 0.5 },
        { height: `${element.offsetHeight}px`, opacity: 1, marginBottom: "0px" },
      ],
      { duration: 420, easing: "cubic-bezier(0.65, 0, 0.35, 1)" }
    ).onfinish = () => (element.style.overflow = "");
  }

  // The fresh body arrives with its final numbers, so the old ones are put back and rolled to the new.
  #animateFrom(before, body, list) {
    const roll = (cell, from) => {
      const to = cell?.textContent.trim();
      if (!cell || !from || !to || from === to) return;
      cell.textContent = from;
      rollText(cell, to, amount(to) < amount(from) ? -1 : 1);
    };

    roll(body.querySelector(".scpd-cart__sum dd"), before.total);

    // The bag has just been emptied: the empty state rises in piece by piece, like a line arriving.
    const empty = body.querySelector(".scpd-cart__empty");
    if (empty && before.lines.size > 0 && !REDUCED.matches) {
      empty.classList.add("is-arriving");
      body.querySelector(".scpd-cart__foot")?.classList.add("is-arriving");
    }

    for (const block of body.querySelectorAll(FOLDING)) {
      if (!before.blocks.has(block.className)) this.#expand(block);
    }
    if (!REDUCED.matches) {
      for (const button of body.querySelectorAll("[data-scpd-add]")) {
        if (before.offers.has(button.dataset.scpdAdd)) continue;
        const card = button.closest(".scpd-cart-offer");
        if (card && !card.closest(".scpd-cart__offers")?.style.overflow) card.classList.add("is-arriving");
      }
    }

    let arrived = null;
    for (const line of body.querySelectorAll("[data-scpd-line]")) {
      const old = before.lines.get(line.dataset.scpdLine);
      if (old) {
        roll(line.querySelector(".scpd-cart__price > span"), old.price);
        roll(line.querySelector(".scpd-cart__qty-value"), old.qty);
      } else if (!REDUCED.matches) {
        line.classList.add("is-arriving");
        line.addEventListener("animationend", () => line.classList.remove("is-arriving"), { once: true });
        arrived ??= line;
      }
    }

    if (arrived && list) {
      const top = arrived.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
      if (top < list.scrollTop || top > list.scrollTop + list.clientHeight - arrived.offsetHeight) {
        list.scrollTo({ top, behavior: "smooth" });
      }
    }
  }

  #focusKey(element) {
    for (const name of ["data-scpd-qty", "data-scpd-remove", "data-scpd-add"]) {
      const value = element.getAttribute(name);
      if (value !== null) {
        const delta = element.dataset.delta ? `[data-delta="${element.dataset.delta}"]` : "";
        return `[${name}="${CSS.escape(value)}"]${delta}`;
      }
    }
    return element.hasAttribute("data-scpd-close") ? "[data-scpd-close]" : null;
  }

  #announce(count) {
    const rendered = Number(this.querySelector("[data-scpd-cart-body]")?.dataset.count);
    const itemCount = count ?? (Number.isFinite(rendered) ? rendered : 0);
    document.dispatchEvent(
      new CartUpdateEvent({}, "scpd-cart", { itemCount, source: "scpd-cart" })
    );
  }

  #say(line, text, persistOnLine = false) {
    const slot = line?.querySelector("[data-scpd-line-message]");
    if (slot) {
      slot.textContent = text || "";
      if (persistOnLine) setTimeout(() => { if (slot.isConnected) slot.textContent = ""; }, 4000);
      return;
    }
    const status = this.querySelector("[data-scpd-cart-status]");
    if (!status) return;
    status.textContent = text || "";
    clearTimeout(this.#status);
    status.getAnimations().forEach((animation) => animation.cancel());
    status.style.overflow = status.style.pointerEvents = "";
    if (text) {
      this.#status = setTimeout(async () => {
        if (!status.isConnected) return;
        await this.#collapse(status);
        status.textContent = "";
        status.getAnimations().forEach((animation) => animation.cancel());
        status.style.overflow = status.style.pointerEvents = "";
      }, 6000);
    }
  }
}

if (!customElements.get("scpd-cart")) customElements.define("scpd-cart", Cart);

// Applies what the free scoop snippet decided on render. Only the scoop the theme added, marked with the
// line property `_free`, is ever changed; scoops the customer added themselves stay as they are.
const SCOOP_UPDATES = {
  add: ({ variant }) => ({
    add: { items: [{ id: Number(variant), quantity: 1, properties: { _free: "First order" } }] },
    update: { attributes: { "Free scoop": "Added" } },
  }),
  remove: ({ key }) => ({ update: { updates: { [key]: 0 }, attributes: { "Free scoop": "" } } }),
  ineligible: ({ key, visitor }) => ({ update: { updates: { [key]: 0 }, attributes: { "Free scoop": `Ineligible:${visitor}` } } }),
  declined: () => ({ update: { attributes: { "Free scoop": "No" } } }),
};

// Each decision runs once per page view, so a cart that does not take the change cannot loop.
const scoopDone = new Set();

class FreeScoop extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", this.#onClick);
    const action = this.dataset.action;
    if (action && !scoopDone.has(action)) {
      scoopDone.add(action);
      this.#apply(SCOOP_UPDATES[action]?.(this.#context));
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#onClick);
  }

  #onClick = (event) => {
    if (!event.target.closest("[data-scpd-free-scoop-again]")) return;
    this.#apply(SCOOP_UPDATES.add(this.#context));
  };

  get #context() {
    const { variant, visitor, key } = this.dataset;
    return { variant, visitor, key };
  }

  async #post(path, body) {
    const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(response.statusText);
    return response.json();
  }

  async #apply(payload) {
    if (!payload) return;
    try {
      if (payload.add) await this.#post("add.js", payload.add);
      const cart = await this.#post("update.js", payload.update);
      // The cart page is drawn on the server; the bag redraws itself on the update event.
      if (document.querySelector(".scpd-cart-page")) location.reload();
      else document.dispatchEvent(new CartUpdateEvent(cart, "scpd-free-scoop", { itemCount: cart.item_count }));
    } catch (error) {
      console.error(error);
    }
  }
}

if (!customElements.get("scpd-free-scoop")) customElements.define("scpd-free-scoop", FreeScoop);
